import { Outlet } from 'react-router';
import type { AdminRole } from '@staff-app/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';

interface Props {
  /** Optional role constraint — e.g. requiredRole="root" for Admin Users page. */
  requiredRole?: AdminRole;
}

/**
 * Route guard that waits for the `staffMe` query to settle, then:
 *   - renders `<Outlet />` if adminUser is populated and (optional) role matches
 *   - renders `<PendingApprovalPage />` otherwise
 *
 * The backend `AdminRoleGuard` is the real enforcement point — this is UX
 * so signed-in users see something intelligible instead of a blank page
 * while queries 403.
 */
export function AdminRoleRoute({ requiredRole }: Props) {
  const adminUser = useAuthStore((s) => s.adminUser);
  const adminUserLoaded = useAuthStore((s) => s.adminUserLoaded);

  if (!adminUserLoaded) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!adminUser) {
    return <PendingApprovalPage />;
  }

  if (requiredRole && adminUser.role !== requiredRole) {
    // Not-root user hit a root-only route — treat as pending (same UX as
    // not-in-admin_users, the backend would 403 any sensitive call anyway).
    return <PendingApprovalPage />;
  }

  return <Outlet />;
}
